/**
 * Copyright (c) 2010-2020 California CAD Solutions, Inc. Licensed subject
 * to an End-User License Agreement included in a separate file named EULA.txt
 * that is distributed with this file.
 */
import {
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges,
    ViewEncapsulation,
} from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { LoggerService } from '../../modules/shared/services/logger.service';
import { DataFormService } from '../../modules/reports/services/dataform.service';
import { StorageService } from '../../modules/shared/services/storage.service';
import { DownloadService } from '../../modules/shared/services/download.service';
import { catchError, concatMap, concatMapTo, map, mergeMap, switchMap, take, tap } from 'rxjs/operators';
import { EMPTY, Observable, of } from 'rxjs';
import { LoadDataForm } from '../../modules/reports/state/dataForm.actions';
import {
    DataFieldValueModel,
    DataFormDefinition,
    DataFormValueModel,
    LoadDataFormPayload,
} from '../../modules/reports/state/dataForm.model';
import { Select, Store } from '@ngxs/store';
import { DataFormState } from '../../modules/reports/state/dataForm.state';
import { DashGisUtil } from '../../modules/shared/dash-gis-util';
import { MatDialog } from '@angular/material/dialog';
import { FileDeleteConfirmComponent } from './file-delete-confirm.component';
import { ShowError } from '../../core/state/notification/notification.actions';
import { StorageDirectUrl, StorageFile } from '../../types';

@Component({
    selector: 'dash-gis-file-download',
    templateUrl: './file-download.component.html',
    encapsulation: ViewEncapsulation.Emulated,
    styleUrls: ['./file-download.component.scss'],
})
export class FileDownloadComponent implements OnInit, OnChanges {
    @Output() changed = new EventEmitter<void>();
    @Output() loaded = new EventEmitter<void>();

    @Input() dataForm: string;
    @Input() fileHash: string;
    @Input() key: string;
    @Input() thumbnailWidth?: number;

    fileType$: Observable<string>;
    icon$: Observable<string>;
    thumbnailUrl$: Observable<string>;

    @Select(DataFormState.definition) private _definition$: Observable<(string) => DataFormDefinition>;
    @Select(DataFormState.value) private _value$: Observable<(string) => DataFormValueModel>;

    constructor(
        private logger: LoggerService,
        private storageService: StorageService,
        private dataFormService: DataFormService,
        private dialog: MatDialog,
        private snackBar: MatSnackBar,
        private store: Store
    ) {}

    ngOnInit() {}

    ngOnChanges(changes: SimpleChanges) {
        if (changes.fileHash && changes.fileHash.previousValue !== changes.fileHash.currentValue) {
            this.fileType$ = this.getFileMimeType$().pipe(
                map((mimeType) => {
                    const reResult = /^([-.\w]+)\/([-.\w]+)/.exec(mimeType);
                    if (reResult && reResult.length === 3) {
                        return reResult[1].toLowerCase() === 'image' ? 'image' : reResult[2];
                    }
                    return mimeType;
                })
            );
            this.icon$ = this.getFileMimeType$().pipe(
                map(this.storageService.mimeTypeToIcon)
            );
            this.thumbnailUrl$ = this.fileType$.pipe(
                mergeMap((fileType) =>
                    fileType === "image" ? this.storageService.getImageThumbnailUrl(this.fileHash, this.thumbnailWidth) : EMPTY
                )
            );
        }
    }

    imageLoaded() {
        this.loaded.emit();
    }

    download() {
        this.storageService
            .retrieveFile(this.fileHash)
            .pipe(
                take(1),
                switchMap((storageObject) => {
                    if (storageObject['content'] !== undefined) {
                        return this.storageService.handleBlob(storageObject as StorageFile);
                    } else if (storageObject['url'] !== undefined) {
                        return this.storageService.handleUrl(storageObject as StorageDirectUrl);
                    } else {
                        this.store.dispatch(new ShowError('Invalid response when retrieving file.'));
                        return EMPTY;
                    }
                })
            )
            .subscribe();
    }

    confirmRemove() {
        this.dialog
            .open(FileDeleteConfirmComponent)
            .afterClosed()
            .subscribe((result: boolean) => {
                if (result) {
                    this.remove();
                }
            });
    }

    remove() {
        const dataFormPayload: LoadDataFormPayload = {
            name: this.dataForm,
            keys: [this.key.toString()],
        };
        this.store
            .dispatch(new LoadDataForm(dataFormPayload))
            .pipe(
                concatMapTo(this.formValue$),
                concatMap((dataFormModel: DataFormValueModel) => {
                    const newModel = DashGisUtil.clone(dataFormModel);
                    newModel.fields.forEach((field: DataFieldValueModel) => {
                        if (field.name.toLowerCase() === 'deleted') {
                            field.value = 1;
                            field.dirty = true;
                        }
                    });
                    return this.dataFormService.saveData(newModel);
                }),
                concatMap(() => this.storageService.removeFile(this.fileHash)),
                tap((result) => {
                    if (result !== true) {
                        throw new Error('Error removing file.');
                    }
                }),
                catchError((err) => {
                    this.logger.error(err);
                    this.snackBar.open(err);
                    return of(false);
                }),
                take(1)
            )
            .subscribe(() => this.changed.emit());
    }

    private getFileMimeType$() {
        return this.storageService.getFileMimeType(this.fileHash);
    }

    private getThumbnail(): Observable<string> {
        if (!this.fileHash) {
            return of('');
        }
        return this.storageService.getFileMimeType(this.fileHash).pipe(
            concatMap((mimeType: string): Observable<string> => {
                if (this.isImageType(mimeType)) {
                    return this.storageService.getImageThumbnailUrl(this.fileHash, this.thumbnailWidth);
                } else {
                    return of('');
                }
            })
        );
    }

    private isImageType(mimeType: string): boolean {
        return mimeType.split('/')[0] === 'image';
    }

    public get formDefinition$() {
        return this._definition$.pipe(map((fn) => fn(this.dataForm)));
    }

    public get formValue$() {
        return this._value$.pipe(map((fn) => fn(this.dataForm)));
    }
}
